import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFi } from '../../contexts/fi-context';
import { useAutoGrowTextarea } from '../../hooks/utils/useAutoGrowTextarea';

export const FiInputBar: React.FC = () => {
  const { sendMessage, isSending, inputHint, inputDraft, setInputDraft, clearInputStarter } = useFi();
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useAutoGrowTextarea(textareaRef, text, 4);

  useEffect(() => {
    if (inputDraft) {
      setText(inputDraft);
      textareaRef.current?.focus();
      const len = inputDraft.length;
      textareaRef.current?.setSelectionRange(len, len);
    }
  }, [inputDraft]);

  useEffect(() => {
    if (inputHint && !inputDraft) {
      textareaRef.current?.focus();
    }
  }, [inputHint, inputDraft]);

  const submit = useCallback(async () => {
    const t = text.trim();
    if (!t || isSending) return;
    setText('');
    clearInputStarter();
    await sendMessage(t);
  }, [text, isSending, sendMessage, clearInputStarter]);

  return (
    <div className="flex w-full shrink-0 flex-col gap-1.5 pt-0.5">
      <div className="flex w-full items-center gap-1 rounded-[20px] border border-[rgba(144,161,185,0.4)] bg-white px-3 py-1.5">
        <textarea
          ref={textareaRef}
          rows={1}
          value={text}
          disabled={isSending}
          onChange={(e) => {
            setText(e.target.value);
            if (inputDraft) setInputDraft('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={inputHint || 'Message Fi…'}
          aria-label={inputHint || 'Message Fi'}
          className={`font-open-sans min-h-[24px] w-full flex-1 resize-none border-0 bg-transparent py-0 text-sm leading-6 tracking-[-0.266px] outline-none focus:ring-0 ${
            inputHint ? 'placeholder:italic placeholder:text-amber-700/70' : 'placeholder:text-muted-text-gray'
          }`}
          style={{ fontVariationSettings: "'wdth' 100" }}
        />
        <button
          type="button"
          aria-label="Send message"
          disabled={isSending || !text.trim()}
          onClick={submit}
          className="text-accent flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M3.4 20.4 21 12 3.4 3.6 3.4 10l12 2-12 2z" />
          </svg>
        </button>
      </div>
    </div>
  );
};
